import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ImageUpload } from "@/components/ImageUpload";
import { AuthModal } from "@/components/AuthModal";
import { toast } from "sonner";

const CATS = ["art", "cars", "jewellery", "fashion", "watches"];
const empty = { title: "", category: "art", maker: "", price: "", year: "", provenance: "", description: "", image: "" };

export default function Consign() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState([]);
  const [authOpen, setAuthOpen] = useState(false);

  const load = () => user && api.get("/consignments/mine").then(({ data }) => setMine(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user]);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { setAuthOpen(true); return; }
    if (!form.image) { toast.error("Please add an image of your piece."); return; }
    setBusy(true);
    try {
      await api.post("/consignments", { ...form, price: parseFloat(form.price) });
      toast.success("Submitted for the curator's review.");
      setForm(empty);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] px-6 pb-32 pt-32 md:px-12 md:pt-40" data-testid="consign-page">
      <div className="mx-auto max-w-[1600px]">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}>
          <p className="text-[11px] uppercase tracking-[0.4em] text-gold">Consign a piece</p>
          <h1 className="font-serif-lux mt-4 text-5xl font-light leading-none text-[#f5f5f0] md:text-8xl">
            Submit the singular
          </h1>
          <p className="mt-6 max-w-xl text-base font-light leading-relaxed text-[#a1a1aa]">
            Offer your one-of-one object to the register. Our curator reviews every submission
            before it is listed. You'll be notified by email once a decision is made.
          </p>
        </motion.div>

        <div className="mt-16 grid grid-cols-1 gap-16 lg:grid-cols-2">
          <form onSubmit={submit} className="space-y-6" data-testid="consign-form">
            <ImageUpload value={form.image} onChange={set("image")} />
            <div className="grid grid-cols-2 gap-4">
              <Field label="Title" testid="consign-title" value={form.title} onChange={set("title")} required />
              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] text-[#52525b]">Category</label>
                <select data-testid="consign-category" value={form.category} onChange={set("category")}
                  className="mt-2 w-full border-b border-white/20 bg-transparent py-3 text-sm capitalize outline-none focus:border-gold">
                  {CATS.map((c) => <option key={c} value={c} className="bg-[#0a0a0a] capitalize">{c}</option>)}
                </select>
              </div>
              <Field label="Maker / Artist" testid="consign-maker" value={form.maker} onChange={set("maker")} required />
              <Field label="Asking price (USD)" testid="consign-price" type="number" value={form.price} onChange={set("price")} required />
              <Field label="Year" testid="consign-year" value={form.year} onChange={set("year")} required />
              <Field label="Provenance" testid="consign-provenance" value={form.provenance} onChange={set("provenance")} required />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-[#52525b]">Description</label>
              <textarea data-testid="consign-description" rows={4} required value={form.description} onChange={set("description")}
                className="mt-2 w-full resize-none border border-white/20 bg-transparent p-3 text-sm outline-none focus:border-gold" />
            </div>
            <button data-testid="consign-submit" type="submit" disabled={busy}
              className="hover-invert w-full border border-[#f5f5f0] bg-[#f5f5f0] px-6 py-4 text-[11px] uppercase tracking-[0.3em] text-black hover:bg-transparent hover:text-[#f5f5f0] disabled:opacity-50">
              {busy ? "Submitting…" : user ? "Submit for review" : "Sign in to submit"}
            </button>
          </form>

          <div>
            <h2 className="font-serif-lux mb-6 text-2xl font-light text-[#f5f5f0]">Your submissions</h2>
            {!user ? (
              <p className="text-sm font-light text-[#52525b]">Sign in to track your consignments.</p>
            ) : mine.length === 0 ? (
              <p className="text-sm font-light text-[#52525b]">No submissions yet.</p>
            ) : (
              <div className="divide-y divide-white/10 border-y border-white/10">
                {mine.map((c) => (
                  <div key={c.id} data-testid="consign-row" className="flex items-center justify-between gap-4 py-5">
                    <div className="flex items-center gap-4">
                      <img src={c.image} alt="" className="h-14 w-14 object-cover" />
                      <div>
                        <p className="font-serif-lux text-xl font-light text-[#f5f5f0]">{c.title}</p>
                        <p className="text-[10px] uppercase tracking-[0.25em] text-[#52525b]">{c.category}</p>
                      </div>
                    </div>
                    <StatusPill status={c.status} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} defaultMode="register" />
    </div>
  );
}

const Field = ({ label, testid, type = "text", value, onChange, required }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.25em] text-[#52525b]">{label}</label>
    <input data-testid={testid} type={type} required={required} value={value} onChange={onChange}
      className="mt-2 w-full border-b border-white/20 bg-transparent py-3 text-sm outline-none focus:border-gold" />
  </div>
);

export const StatusPill = ({ status }) => {
  const map = {
    pending: "border-gold/40 text-gold",
    approved: "border-emerald-500/40 text-emerald-400",
    accepted: "border-emerald-500/40 text-emerald-400",
    rejected: "border-red-500/40 text-red-400",
    declined: "border-red-500/40 text-red-400",
  };
  return (
    <span className={`whitespace-nowrap border px-3 py-1 text-[9px] uppercase tracking-[0.25em] ${map[status] || "border-white/20 text-[#a1a1aa]"}`}>
      {status}
    </span>
  );
};
